import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { type ChangeRequestSet } from "@njbsoft/field-sync";
import { XCircleIcon } from "@/components/icons";
import { RequiresConnection } from "@/components/requires-connection";
import { EmptyState, PageSkeleton } from "@/components/ui";
import {
  ItemTransitions,
  SetTransitions,
} from "@/components/tester/change-requests/change-request-transitions";
import { awaitsResubmission } from "@/components/tester/change-requests/change-request-vocabulary";
import { DetailFacilityOutcome } from "@/components/tester/change-requests/detail-facility-outcome";
import { DetailHeader } from "@/components/tester/change-requests/detail-header";
import { DetailItems } from "@/components/tester/change-requests/detail-items";
import { DetailMessages } from "@/components/tester/change-requests/detail-messages";
import { DetailPhotos } from "@/components/tester/change-requests/detail-photos";
import { DetailProvenance } from "@/components/tester/change-requests/detail-provenance";
import { DetailTimeline } from "@/components/tester/change-requests/detail-timeline";
import { ResubmitOpen } from "@/components/tester/change-requests/resubmit-open";
import { queuedResubmissions } from "@/components/tester/change-requests/staging/resubmit";
import { testerDetailLink } from "@/components/tester/change-requests/tester-detail-link";
import { FieldAccessEnded } from "@/components/tester/chrome/field-access-ended";
import { useInvalidateAfterFieldworkRun } from "@/hooks/use-fieldwork-sync";
import { useIsOnline } from "@/hooks/use-is-online";
import { useMyChangeRequestSet } from "@/hooks/use-my-change-request-set";
import { useOpenChangeRequestThread } from "@/hooks/use-open-change-request-thread";
import { useToast } from "@/hooks/use-toast";
import { formatDateOnly } from "@/lib/dates";
import { useFieldworkStorage } from "@/lib/fieldwork/storage-provider";

interface ChangeRequestSearch {
  /** The request whose Revise and resubmit is open. */
  resubmit?: string;
}

export const Route = createFileRoute("/_company/change-requests/$setId")({
  validateSearch: (search: Record<string, unknown>): ChangeRequestSearch => ({
    resubmit:
      typeof search.resubmit === "string" && search.resubmit !== "" ? search.resubmit : undefined,
  }),
  component: ChangeRequestDetailPage,
});

/**
 * One of the tester's own change-request sets: every request's outcome, what
 * the tester can still do about it, and the conversation with the utility.
 * Online, the server's read; offline, the bundle's copy of an open set, or an
 * honest in-page "needs a connection" for any other.
 */
function ChangeRequestDetailPage() {
  const { setId } = Route.useParams();
  const online = useIsOnline();
  return (
    <div className="space-y-6 p-4 sm:p-8">
      {online ? <OnlineDetail setId={setId} /> : <OfflineDetail setId={setId} />}
    </div>
  );
}

function OnlineDetail({ setId }: { setId: string }) {
  const { t } = useTranslation("change-requests");
  const navigate = useNavigate();
  const query = useMyChangeRequestSet(setId);

  if (query.isPending) return <PageSkeleton />;
  if (query.error instanceof ApiError && query.error.status === 403) return <FieldAccessEnded />;
  if (query.error instanceof ApiError && query.error.status === 404) {
    return (
      <EmptyState
        title={t("detail.notFound")}
        actionLabel={t("detail.back")}
        onAction={() => void navigate({ to: "/my-work", search: { tab: "requests" } })}
      />
    );
  }
  if (query.isError) {
    return (
      <EmptyState
        icon={<XCircleIcon className="text-danger" />}
        title={t("detail.loadError")}
        actionLabel={t("states.retry")}
        onAction={() => void query.refetch()}
      />
    );
  }
  return <ChangeRequestDetail set={query.data} online />;
}

/**
 * Offline, the bundle holds the sets the tester can still act on — those
 * needing them and those pending review. Any other set is a server read.
 */
function OfflineDetail({ setId }: { setId: string }) {
  const { t } = useTranslation("change-requests");
  const { storage } = useFieldworkStorage();
  const [read, setRead] = useState<{ set: ChangeRequestSet | null } | null>(null);

  useEffect(() => {
    if (storage === null) return;
    let stale = false;
    storage
      .listBundleChangeRequestSets()
      .then((sets) => {
        if (!stale) setRead({ set: sets.find((set) => set.id === setId) ?? null });
      })
      .catch((err: unknown) => {
        if (stale) return;
        console.error("offline change-request set read failed", err);
        setRead({ set: null });
      });
    return () => {
      stale = true;
    };
  }, [storage, setId]);

  if (read === null) return <PageSkeleton />;
  if (read.set === null) {
    return (
      <RequiresConnection
        showTesterCta={false}
        title={t("detail.offline.title")}
        body={t("detail.offline.body")}
      />
    );
  }
  return <ChangeRequestDetail set={read.set} online={false} />;
}

function ChangeRequestDetail({ set, online }: { set: ChangeRequestSet; online: boolean }) {
  const { t } = useTranslation("change-requests");
  const navigate = useNavigate();
  const toast = useToast();
  const { storage } = useFieldworkStorage();
  const openThread = useOpenChangeRequestThread();
  const { resubmit } = Route.useSearch();
  const revised = set.items.find((item) => item.id === resubmit);
  // A resubmission this device queued answers its request before the server
  // has it: Revise is not offered twice.
  const [queued, setQueued] = useState<ReadonlyMap<string, string>>(new Map());
  const [queuedReads, setQueuedReads] = useState(0);
  // A drained resubmit moved the set's server truth while this screen holds
  // its cached read — the completion marks the run's query families stale
  // through the shared settle invalidation (the resubmit's drain runs the
  // engine directly and never passes through the sync hook's own sweep), so
  // the detail refetches instead of serving pre-resubmit state through its
  // staleTime window.
  const invalidateAfterRun = useInvalidateAfterFieldworkRun();

  useEffect(() => {
    if (storage === null) return;
    let stale = false;
    storage.listChangeRequestSetRows().then(
      (rows) => {
        if (!stale) setQueued(queuedResubmissions(rows));
      },
      (err: unknown) => {
        if (!stale) console.error("queued resubmission read failed", err);
      },
    );
    return () => {
      stale = true;
    };
  }, [storage, queuedReads]);

  // The editor's entry is replaced, so Back returns to the detail and never reopens it.
  const closeResubmit = useCallback(() => {
    void navigate({
      to: "/change-requests/$setId",
      params: { setId: set.id },
      search: {},
      replace: true,
    });
  }, [navigate, set.id]);

  const messageUtility = () => {
    openThread.mutate(
      { setId: set.id },
      {
        onSuccess: (thread) => {
          void navigate({ to: "/messages", search: { thread: thread.thread_id } });
        },
        onError: () => {
          toast.error("field-work:home.rejections.threadFailed");
        },
      },
    );
  };

  return (
    <>
      <DetailHeader
        set={set}
        backTo="/my-work"
        backSearch={{ tab: "requests" }}
        backLabel={t("detail.back")}
      />
      <SetTransitions set={set} />
      <DetailFacilityOutcome set={set} />
      <DetailPhotos set={set} />
      <DetailItems
        set={set}
        link={testerDetailLink}
        audience="tester"
        renderActions={(item) => {
          const queuedAt = queued.get(item.id);
          if (queuedAt !== undefined) {
            return (
              <p className="text-sm text-on-surface">
                {t("list.resubmitted", { date: formatDateOnly(queuedAt) })}
              </p>
            );
          }
          return (
            <ItemTransitions
              set={set}
              item={item}
              onRevise={(revised) => {
                void navigate({
                  to: "/change-requests/$setId",
                  params: { setId: set.id },
                  search: { resubmit: revised.id },
                });
              }}
            />
          );
        }}
      />
      <DetailProvenance set={set} />
      <DetailTimeline set={set} audience="tester" />
      <DetailMessages
        set={set}
        link={testerDetailLink}
        action={{
          onMessageUtility: messageUtility,
          disabled: !online || openThread.isPending,
        }}
      />
      {online &&
        revised !== undefined &&
        awaitsResubmission(revised) &&
        !queued.has(revised.id) && (
          <ResubmitOpen
            key={revised.id}
            set={set}
            item={revised}
            onClose={closeResubmit}
            onResubmitted={() => {
              invalidateAfterRun();
              setQueuedReads((count) => count + 1);
            }}
          />
        )}
    </>
  );
}
